import { Button } from '@calizahq/react-components-v2'
import React, { useState } from 'react'

import { useAlertContext } from '@/context'
import { HomeIssueMutations } from '@/graphql/mutations/homeIssue.mutations'
import { useMobile } from '@/hooks'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useGraphqlMutation } from '@/hooks/useGraphql'

type Props = {
  homeIssueId: number
  refetchDetails: () => void
  toggle: () => void
}

export const ResolveHomeIssue = ({ toggle, homeIssueId, refetchDetails }: Props) => {
  const { isTablet } = useMobile()
  const { setAlert } = useAlertContext()
  const { captureGeolocation } = useGeolocation()
  const [isCapturing, setIsCapturing] = useState(false)
  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(HomeIssueMutations.resolveHomeIssue(), {
    onSuccess: () => {
      toggle()
      refetchDetails()
      setAlert('Home Issue resolved', 'success')
    },
  })

  const handleResolveHomeIssue = async () => {
    if (isCapturing) return
    setIsCapturing(true)
    try {
      const geolocation = await captureGeolocation()
      mutate({ homeIssueId, geolocation })
    } finally {
      setIsCapturing(false)
    }
  }

  return (
    <div className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
      <h5 className="mb-4 text-header-sm leading-120 md:text-header-md">
        Are you sure you want to resolve this Home Issue?
      </h5>
      <p className="mb-4 text-p-xs md:mb-8">
        You cannot reopen a Resolved Home Issue.
        <br />
        If you discover the Issue is unresolved, you must report a new one.
      </p>
      <div className="flex flex-col-reverse justify-between md:flex-row">
        <Button
          className="w-full bg-action-secondary md:flex md:w-auto"
          onClick={toggle}
          size={isTablet ? 'medium' : 'small'}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
          disabled={isCapturing || isMutationLoading}
          isLoading={isCapturing || isMutationLoading}
          onClick={handleResolveHomeIssue}
          size={isTablet ? 'medium' : 'small'}
        >
          Resolve
        </Button>
      </div>
    </div>
  )
}
